import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal, Button } from "rsuite";
import { getClientMediaPresignedUrls } from "../../../../api";
import { getClientLogo } from "../../../../utils/clientLogos";
import MailOpenIcon from "../../../../assets/img/action-center/mail-open.svg";
import FileAttachedIcon from "../../../../assets/img/action-center/file-attached.svg";
import FilePptIcon from "../../../../assets/img/action-center/file-ppt.svg";
import FilePdfIcon from "../../../../assets/img/action-center/file-pdf.svg";
import DownloadIcon from "../../../../assets/img/action-center/download.svg";
import ChevronRightWhiteIcon from "../../../../assets/img/action-center/chevron-right-white.svg";
import {
  MODAL_TITLE,
  BANNER_PREFIX,
  FACILITATING_NOTE,
  INVITATION_PREFIX,
  INVITATION_SUFFIX,
  DOWNLOAD_HEADING_BOLD,
  DOWNLOAD_HEADING_REST,
  REVIEW_PROMO_SETUP_LABEL,
  BUYER_FALLBACK_LABEL,
  BUYER_AVATAR_FALLBACK_COLOR,
} from "../campaignConstants";
import "./CampaignReviewModal.scss";

const getFileTypeIcon = (filename, contentType) => {
  const ext = (filename || "").toLowerCase().split(".").pop();
  const ct = (contentType || "").toLowerCase();
  if (ct.includes("pdf") || ext === "pdf") return FilePdfIcon;
  if (
    ct.includes("powerpoint") ||
    ct.includes("presentation") ||
    ext === "ppt" ||
    ext === "pptx"
  )
    return FilePptIcon;
  return FileAttachedIcon;
};

const formatBytes = (raw) => {
  const bytes = Number(raw);
  if (!Number.isFinite(bytes) || bytes <= 0) return null;
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}Kb`;
  if (bytes < 1024 * 1024 * 1024) return `${Math.round(bytes / 1024 / 1024)}Mb`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)}Gb`;
};

const triggerDownload = async (url, filename) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("fetch failed");
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename || "download";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  } catch {
    // S3 CORS not configured for this origin — fall back to opening the
    // presigned URL so the user still gets the file.
    globalThis.open(url, "_blank", "noopener,noreferrer");
  }
};

const CampaignReviewModal = ({ data, onClose, onActivate }) => {
  const [documents, setDocuments] = useState([]);

  const mediaIds = data?.mediaIds;
  const clientId = data?.ownerId;

  useEffect(() => {
    if (!Array.isArray(mediaIds) || mediaIds.length === 0) {
      setDocuments([]);
      return;
    }
    getClientMediaPresignedUrls(mediaIds, clientId)
      .then((res) => {
        const list = res?.data?.data ?? res?.data ?? [];
        setDocuments(Array.isArray(list) ? list : []);
      })
      .catch(() => setDocuments([]));
  }, [mediaIds, clientId]);

  if (!data) return null;

  const { recommendationId, campaignName, ownerName, description } = data;
  const buyerLabel = ownerName || BUYER_FALLBACK_LABEL;
  const buyerLogo = getClientLogo(ownerName);

  return (
    <Modal
      className="campaign-review-modal"
      open
      size="sm"
      backdrop="static"
      onClose={onClose}
    >
      <Modal.Header>
        <Modal.Title>{MODAL_TITLE}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="crm-banner">
          <span>
            {BANNER_PREFIX}
            {buyerLabel}. <strong>{FACILITATING_NOTE}</strong>.
          </span>
        </div>

        <div className="crm-invitation-card">
          <div className="crm-buyer-row">
            <div className="crm-buyer-avatar">
              {buyerLogo ? (
                <img
                  src={buyerLogo}
                  alt={buyerLabel}
                  className="crm-buyer-avatar-img"
                />
              ) : (
                <span
                  data-testid={`campaign-review-fallback-${recommendationId}`}
                  className="crm-buyer-avatar-fallback"
                  style={{ backgroundColor: BUYER_AVATAR_FALLBACK_COLOR }}
                />
              )}
            </div>
            <div className="crm-buyer-meta">
              <span className="crm-buyer-name">{buyerLabel}</span>
              <span className="crm-buyer-subtitle">
                {INVITATION_PREFIX}
                {campaignName && (
                  <strong className="crm-promo-name">{campaignName}</strong>
                )}
                {INVITATION_SUFFIX}
              </span>
            </div>
          </div>

          <div className="crm-invitation-divider" />

          <div className="crm-content-row">
            <div className="crm-content-icon">
              <img src={MailOpenIcon} alt="" />
            </div>
            <div className="crm-content-body">
              <div className="crm-content-text-group">
                <p className="crm-content-text">{description}</p>
              </div>

              {documents.length > 0 && (
                <div className="crm-docs-section">
                  <p className="crm-docs-heading">
                    <strong>{DOWNLOAD_HEADING_BOLD}</strong>
                    {DOWNLOAD_HEADING_REST}
                  </p>
                  <div className="crm-docs-list">
                    {documents.map((doc) => {
                      const sizeLabel = formatBytes(doc.sizeBytes);
                      const iconSrc = getFileTypeIcon(
                        doc.originalFilename,
                        doc.contentType
                      );
                      return (
                        <div key={doc.id} className="crm-doc-card">
                          <div className="crm-doc-icon">
                            <img src={iconSrc} alt="" />
                          </div>
                          <div className="crm-doc-card-divider" />
                          <div className="crm-doc-card-info">
                            <div className="crm-doc-card-text">
                              <span className="crm-doc-card-name">
                                {doc.originalFilename}
                              </span>
                              {sizeLabel && (
                                <span className="crm-doc-card-size">
                                  {sizeLabel}
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              className="crm-doc-card-download"
                              onClick={() =>
                                triggerDownload(doc.url, doc.originalFilename)
                              }
                              aria-label={`Download ${doc.originalFilename}`}
                            >
                              <img src={DownloadIcon} alt="" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          appearance="primary"
          className="activate-now-btn"
          onClick={() => onActivate?.()}
        >
          {REVIEW_PROMO_SETUP_LABEL}{" "}
          <img src={ChevronRightWhiteIcon} alt="" className="btn-chevron" />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

CampaignReviewModal.propTypes = {
  data: PropTypes.shape({
    recommendationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    propertyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    campaignName: PropTypes.string,
    ownerName: PropTypes.string,
    ownerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    description: PropTypes.string,
    mediaIds: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ),
    expiryDate: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onActivate: PropTypes.func.isRequired,
};

export default CampaignReviewModal;
